import { Locale } from "@redotech/crdb-enums/common/locale";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { getLocaleName } from "@redotech/redo-i18n/locales";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

function languageItem(
  language: Locale,
  displayLocale: string,
  stale?: ReadonlySet<Locale>,
): BadgeRedoListItem<Locale> {
  return {
    type: "badge",
    id: language,
    value: language,
    badge: {
      text: getLocaleName(language, displayLocale),
      color: stale?.has(language) ? "warning" : "gray",
    },
  };
}

/**
 * Language selection for a set of translated fields: adding generates
 * translations, removing asks first. Callers own loading and saving.
 */
export const TranslationLanguagePicker = memo(
  function TranslationLanguagePicker({
    languages,
    selected,
    pendingLanguage,
    staleLanguages,
    canEdit,
    mutating,
    loading,
    loadFailed,
    languageToRemove,
    removalCount,
    removalPending,
    removalScope,
    onAdd,
    onRequestRemoval,
    onConfirmRemoval,
    onCancelRemoval,
  }: {
    languages: readonly Locale[];
    selected: readonly Locale[];
    pendingLanguage: Locale | undefined;
    staleLanguages: ReadonlySet<Locale>;
    canEdit: boolean;
    mutating: boolean;
    loading: boolean;
    loadFailed: boolean;
    languageToRemove: Locale | undefined;
    removalCount: number;
    removalPending: boolean;
    /** What the removed translations belong to, for the confirmation copy. */
    removalScope: "step" | "page";
    onAdd: (language: Locale) => void;
    onRequestRemoval: (language: Locale) => void;
    onConfirmRemoval: () => void;
    onCancelRemoval: () => void;
  }) {
    const { t, i18n } = useTranslation();
    const displayLocale = i18n.language;
    const selectedWithPending = useMemo(
      () =>
        pendingLanguage === undefined || selected.includes(pendingLanguage)
          ? selected
          : [...selected, pendingLanguage],
      [selected, pendingLanguage],
    );
    const options = useMemo(
      () => languages.map((language) => languageItem(language, displayLocale)),
      [languages, displayLocale],
    );
    const selectedOptions = useMemo(
      () =>
        selectedWithPending.map((language) =>
          languageItem(language, displayLocale, staleLanguages),
        ),
      [selectedWithPending, displayLocale, staleLanguages],
    );

    const selectionChanged = useHandler(
      (items: BadgeRedoListItem<Locale>[]) => {
        const next = new Set(items.map((item) => item.value));
        const added = languages.find(
          (language) =>
            next.has(language) && !selectedWithPending.includes(language),
        );
        if (!added) {
          // Deselection only asks; rows are deleted after the modal's confirm.
          const removed = selected.find((language) => !next.has(language));
          if (removed) {
            onRequestRemoval(removed);
          }
          return;
        }
        onAdd(added);
      },
    );

    return (
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {t("Translation")}
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {t("Select the languages you want this page translated into.")}
          </Text>
        </Flex>
        {loadFailed ? (
          <Text fontSize="xs" textColor="error">
            {t("Failed to load translation languages.")}
          </Text>
        ) : (
          <RedoTagsDropdownInput
            disabled={!canEdit || mutating}
            label={t("Languages")}
            loading={loading}
            options={options}
            placeholder={t("Select languages...")}
            selectedOptions={selectedOptions}
            setSelectedOptions={selectionChanged}
            size="xs"
          />
        )}
        {staleLanguages.size > 0 && (
          <Text fontSize="xs" textColor="warning">
            {t(
              "Highlighted languages no longer match the current English. Open a field's translations to review and save them.",
            )}
          </Text>
        )}
        {languageToRemove && (
          <TranslationLanguageRemovalModal
            languageName={getLocaleName(languageToRemove, displayLocale)}
            onClose={onCancelRemoval}
            onConfirm={onConfirmRemoval}
            pending={removalPending}
            scope={removalScope}
            translationCount={removalCount}
          />
        )}
      </Flex>
    );
  },
);

const TranslationLanguageRemovalModal = memo(
  function TranslationLanguageRemovalModal({
    languageName,
    translationCount,
    scope,
    pending,
    onClose,
    onConfirm,
  }: {
    languageName: string;
    translationCount: number;
    scope: "step" | "page";
    pending: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }) {
    const { t } = useTranslation();
    const subtitle =
      scope === "step"
        ? t(
            "This will delete {{count}} {{language}} translations saved for this step, including any you edited by hand. This can't be undone.",
            { count: translationCount, language: languageName },
          )
        : translationCount === 1
          ? t(
              "This will delete the one {{language}} translation saved for this page, including any you edited by hand. This can't be undone.",
              { language: languageName },
            )
          : t(
              "This will delete all {{translationCount}} {{language}} translations saved for this page, including any you edited by hand. This can't be undone.",
              { translationCount, language: languageName },
            );
    return (
      <RedoModal
        modalSize="sm"
        onModalCloseRequested={pending ? () => {} : onClose}
        primaryButton={{
          text: t("Remove language"),
          onClick: onConfirm,
          pending,
          theme: "destructive",
          size: "sm",
        }}
        secondaryButton={{
          text: t("Cancel"),
          onClick: onClose,
          disabled: pending,
          size: "sm",
        }}
        subtitle={subtitle}
        title={t("Remove {{language}}?", { language: languageName })}
      />
    );
  },
);
